import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { 
  Award, 
  Search,
  Briefcase,
  ChevronRight,
  Globe2,
  BookOpen,
  Keyboard,
  History,
  MessageSquare,
  BarChart3,
  FileText,
  Settings,
  GraduationCap,
  Theater,
  Loader2,
  ArrowUpDown,
  X,
  ChevronLeft,
  ChevronRightIcon,
  Users,
  Scale
} from 'lucide-react';

const StaffingTraining = () => {
  const { api, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    rpat: { progress: 0, completed: 0, total: 23 },
    recruiterSims: { completed: 0, avgScore: 0 },
    jobSims: { completed: 0, total: 16 },
    sourcingLabs: { completed: 0, total: 10 },
    globalModules: { badges: 0, total: 5 },
    eeoc: { completed: 0, total: 0, progress: 0 }
  });
  
  // Admin stats (aggregate across all users)
  const [adminStats, setAdminStats] = useState({
    topics_done: 0,
    simulations: 0,
    job_sims: 0,
    labs_done: 0,
    badges: 0
  });
  
  // Modal state for detail view
  const [detailModal, setDetailModal] = useState({
    open: false,
    category: null,
    title: '',
    loading: false,
    items: [],
    pagination: { page: 1, per_page: 20, total: 0, total_pages: 0 },
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  const hasEeocAccess = isAdmin || user?.feature_access?.eeoc_training !== false;

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch user's personal progress data
      const results = await Promise.allSettled([
        api.get('/rpat/dashboard'),
        api.get('/simulations/overview'),
        api.get('/job-simulations/analytics/summary'),
        api.get('/rpat/global/badges'),
        hasEeocAccess ? api.get('/eeoc-training/overview') : Promise.reject(new Error('no access'))
      ]);
      
      const [rpatRes, recSimRes, jobSimRes, globalRes, eeocRes] = results;
      const eeocSummary = eeocRes.value?.data?.summary || {};
      
      // Extract job simulation data from correct endpoint
      const jobSimData = jobSimRes.value?.data || {};
      
      setStats({
        rpat: {
          progress: rpatRes.value?.data?.progress?.percentage || rpatRes.value?.data?.overall_progress_percentage || 0,
          completed: rpatRes.value?.data?.progress?.completed_topics || rpatRes.value?.data?.completed_topics_count || 0,
          total: rpatRes.value?.data?.progress?.total_topics || rpatRes.value?.data?.total_topics_count || 23
        },
        recruiterSims: {
          completed: recSimRes.value?.data?.total_simulations || 0,
          avgScore: recSimRes.value?.data?.average_score || 0
        },
        jobSims: {
          completed: jobSimData.skills_completed || jobSimData.total_skills_completed || 0,
          total: jobSimData.total_skills || 16
        },
        sourcingLabs: {
          completed: rpatRes.value?.data?.sourcing_labs_completed || 0,
          total: 10
        },
        globalModules: {
          badges: globalRes.value?.data?.badges?.length || 0,
          total: 5
        },
        eeoc: {
          completed: eeocSummary.completed_modules || 0,
          total: eeocSummary.total_modules || 0,
          progress: eeocSummary.progress || 0
        }
      });
      
      // If admin, also fetch aggregate stats
      if (isAdmin) {
        try {
          const adminRes = await api.get('/dashboard/admin/staffing-hub-stats');
          setAdminStats(adminRes.data);
        } catch (err) {
          console.error('Failed to fetch admin stats:', err);
        }
      }
    } catch (error) {
      console.error('Failed to fetch training stats:', error);
    } finally {
      setLoading(false);
    }
  }, [api, isAdmin, hasEeocAccess]);

  // Fetch detail data for a category
  const fetchDetailData = useCallback(async (category, page = 1, sortBy = 'created_at', sortOrder = 'desc') => {
    try {
      setDetailModal(prev => ({ ...prev, loading: true }));
      
      const res = await api.get(`/dashboard/admin/staffing-hub-details/${category}`, {
        params: { page, per_page: 20, sort_by: sortBy, sort_order: sortOrder }
      });
      
      setDetailModal(prev => ({
        ...prev,
        loading: false,
        items: res.data.items || [],
        pagination: res.data.pagination || { page: 1, per_page: 20, total: 0, total_pages: 0 },
        sortBy,
        sortOrder
      }));
    } catch (err) {
      console.error('Failed to fetch detail data:', err);
      toast.error('Failed to load details');
      setDetailModal(prev => ({ ...prev, loading: false }));
    }
  }, [api]);

  // Open detail modal
  const openDetailModal = (category, title) => {
    if (!isAdmin) return;
    
    setDetailModal({
      open: true,
      category,
      title,
      loading: true,
      items: [],
      pagination: { page: 1, per_page: 20, total: 0, total_pages: 0 },
      sortBy: 'created_at',
      sortOrder: 'desc'
    });
    
    fetchDetailData(category, 1, 'created_at', 'desc');
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    fetchDetailData(detailModal.category, newPage, detailModal.sortBy, detailModal.sortOrder);
  };

  // Handle sort change
  const handleSortChange = (field) => {
    const newOrder = detailModal.sortBy === field && detailModal.sortOrder === 'desc' ? 'asc' : 'desc';
    fetchDetailData(detailModal.category, 1, field, newOrder);
  };

  // Close modal
  const closeDetailModal = () => {
    setDetailModal(prev => ({ ...prev, open: false }));
  };

  // Get column headers based on category
  const getColumnsForCategory = (category) => {
    switch (category) {
      case 'topics':
        return [
          { key: 'user_name', label: 'User', sortable: true },
          { key: 'topic_name', label: 'Topic', sortable: true },
          { key: 'score', label: 'Score', sortable: true },
          { key: 'completed_at', label: 'Completed', sortable: true }
        ];
      case 'simulations':
        return [
          { key: 'user_name', label: 'User', sortable: true },
          { key: 'scenario', label: 'Scenario', sortable: true },
          { key: 'score', label: 'Score', sortable: true },
          { key: 'completed_at', label: 'Completed', sortable: true }
        ];
      case 'job_sims':
        return [
          { key: 'user_name', label: 'User', sortable: true },
          { key: 'industry', label: 'Industry', sortable: true },
          { key: 'skill', label: 'Skill', sortable: true },
          { key: 'score', label: 'Score', sortable: true }
        ];
      case 'labs':
        return [
          { key: 'user_name', label: 'User', sortable: true },
          { key: 'lab_name', label: 'Lab', sortable: true },
          { key: 'score', label: 'Score', sortable: true },
          { key: 'completed_at', label: 'Completed', sortable: true }
        ];
      case 'badges':
        return [
          { key: 'user_name', label: 'User', sortable: true },
          { key: 'badge_name', label: 'Badge', sortable: true },
          { key: 'badge_type', label: 'Type', sortable: true },
          { key: 'earned_at', label: 'Earned', sortable: true }
        ];
      default:
        return [];
    }
  };

  // Format cell value
  const formatCellValue = (item, key) => {
    const value = item[key];
    if (key === 'score') {
      return value ? `${Math.round(value)}%` : '—';
    }
    if (key === 'created_at' || key === 'completed_at' || key === 'earned_at') {
      if (!value) return '—';
      try {
        return formatDistanceToNow(new Date(value), { addSuffix: true });
      } catch {
        return '—';
      }
    }
    if (key === 'badge_type') {
      return value === 'global_compliance' ? 'Global' : 'Achievement';
    }
    if (key === 'scenario') {
      return value ? value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '—';
    }
    return value || '—';
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Training modules configuration - Split into two categories
  
  // Simulations & Practice modules (4 modules)
  const simulationsModules = [
    {
      id: 'recruiter-sims',
      title: 'Recruiter Simulations',
      description: 'Practice real recruiter conversations with AI-powered role-play including client calls, candidate screening, and interviews.',
      icon: MessageSquare,
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30',
      borderColor: 'border-blue-200 dark:border-blue-800',
      path: '/simulation',
      statsLabel: 'Completed',
      statsValue: stats.recruiterSims.completed,
      progress: null,
      features: ['Client Calls', 'Candidate Screening', 'Objection Handling', 'Salary Negotiation'],
      badge: stats.recruiterSims.avgScore >= 80 ? `Avg: ${stats.recruiterSims.avgScore}%` : null
    },
    {
      id: 'job-sims',
      title: 'Job Simulations',
      description: 'Industry-specific simulations with progressive difficulty. Master recruitment across IT, Healthcare, Engineering, and more.',
      icon: Briefcase,
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30',
      borderColor: 'border-emerald-200 dark:border-emerald-800',
      path: '/job-simulations-analytics',
      statsLabel: 'Levels',
      statsValue: `${stats.jobSims.completed}/${stats.jobSims.total}`,
      progress: (stats.jobSims.completed / stats.jobSims.total) * 100,
      features: ['IT', 'Engineering', 'Healthcare', 'Progressive Levels'],
      badge: null
    },
    {
      id: 'roleplay-lab',
      title: 'Role Play Lab',
      description: 'AI-powered conversation practice with realistic personas. Master EEOC compliance, creative outreach, and negotiation skills.',
      icon: Theater,
      color: 'from-indigo-500 to-violet-600',
      bgColor: 'bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/30',
      borderColor: 'border-indigo-200 dark:border-indigo-800',
      path: '/roleplay-analytics',
      statsLabel: 'Scenarios',
      statsValue: '24 total',
      progress: null,
      features: ['EEOC Compliance', 'Creative Outreach', 'Client Mastery', 'Negotiation'],
      badge: 'NEW'
    },
    {
      id: 'sourcing-labs',
      title: 'Sourcing Labs',
      description: 'Hands-on sourcing exercises to sharpen your candidate search skills with Boolean search and talent mapping.',
      icon: Search,
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30',
      borderColor: 'border-amber-200 dark:border-amber-800',
      path: '/sourcing-labs-analytics',
      statsLabel: 'Labs',
      statsValue: `${stats.sourcingLabs.completed}/${stats.sourcingLabs.total}`,
      progress: (stats.sourcingLabs.completed / stats.sourcingLabs.total) * 100,
      features: ['Boolean Search', 'LinkedIn Sourcing', 'Talent Mapping', 'Pipeline Building'],
      badge: null
    }
  ];

  // Training & Certifications modules (3 modules)
  const trainingModules = [
    {
      id: 'rpat',
      title: 'R-PAT Training',
      description: 'Recruitment Process Autonomous Training - Master the fundamentals of staffing and recruitment through 4 comprehensive phases.',
      icon: GraduationCap,
      color: 'from-violet-500 to-purple-600',
      bgColor: 'bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30',
      borderColor: 'border-violet-200 dark:border-violet-800',
      path: '/rpat',
      statsLabel: 'Topics',
      statsValue: `${stats.rpat.completed}/${stats.rpat.total}`,
      progress: stats.rpat.progress,
      features: ['4 Training Phases', 'Voice Lessons', 'Assessments', 'Typing Tests', 'Playbooks'],
      badge: stats.rpat.completed >= stats.rpat.total ? 'Certified' : null
    },
    {
      id: 'arc',
      title: 'Advanced Recruitment Certification',
      description: 'Comprehensive 26-chapter certification program. Master technical recruiting with voice quizzes, AI feedback, and certification exam.',
      icon: Award,
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30',
      borderColor: 'border-amber-200 dark:border-amber-800',
      path: '/arc',
      statsLabel: 'Chapters',
      statsValue: '26 total',
      progress: null,
      features: ['Voice Quizzes', 'AI Feedback', 'Audio Narration', 'Final Exam', 'Certification'],
      badge: 'PREMIUM',
      isPremium: true
    },
    {
      id: 'global-compliance',
      title: 'Global Compliance',
      description: 'Region-specific recruitment compliance training covering US, UK, Australia, Canada, and Germany.',
      icon: Globe2,
      color: 'from-rose-500 to-pink-600',
      bgColor: 'bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30',
      borderColor: 'border-rose-200 dark:border-rose-800',
      path: '/rpat/global',
      statsLabel: 'Badges',
      statsValue: `${stats.globalModules.badges}/${stats.globalModules.total}`,
      progress: (stats.globalModules.badges / stats.globalModules.total) * 100,
      features: ['🇺🇸 US H-1B/EEOC', '🇬🇧 UK Tier 2/GDPR', '🇦🇺 AU 482', '🇨🇦 Canada', '🇩🇪 Germany'],
      badge: stats.globalModules.badges > 0 ? `${stats.globalModules.badges} Badge${stats.globalModules.badges > 1 ? 's' : ''}` : null
    },
    {
      id: 'eeoc-training',
      title: 'EEOC Training',
      description: 'Voice role-play EEOC compliance training - speak with candidates, get graded rule by rule, and learn from personal coaching.',
      icon: Scale,
      color: 'from-sky-500 to-blue-600',
      bgColor: 'bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/30 dark:to-blue-950/30',
      borderColor: 'border-sky-200 dark:border-sky-800',
      path: '/eeoc-training',
      statsLabel: stats.eeoc.total > 0 ? 'Modules' : '',
      statsValue: stats.eeoc.total > 0 ? `${stats.eeoc.completed}/${stats.eeoc.total}` : 'Coming soon',
      progress: stats.eeoc.total > 0 ? stats.eeoc.progress : null,
      features: ['Voice Scenarios', 'Rule-by-rule Scoring', 'Coaching & Drills', 'Compliance Trends'],
      badge: 'NEW'
    }
  ].filter((module) => module.id !== 'eeoc-training' || hasEeocAccess);

  // Quick actions
  const quickActions = [
    { label: 'Playbooks', icon: BookOpen, path: '/rpat/playbooks', desc: 'Reference materials' },
    { label: 'Typing Tests', icon: Keyboard, path: '/rpat/typing-tests', desc: 'Speed & accuracy' },
    { label: 'Sim History', icon: History, path: '/simulation-history', desc: 'Past sessions' },
    { label: 'Certificates', icon: Award, path: '/rpat/certificate', desc: 'Your achievements' }
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[1, 2, 3, 4, 5, 6, 7].map(i => <Skeleton key={i} className="h-64" />)}
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-violet-600 via-blue-600 to-emerald-600 bg-clip-text text-transparent">
              Staffing Training Hub
            </h1>
            <p className="text-muted-foreground mt-1">
              {isAdmin 
                ? 'Manage and monitor all training modules and learner progress'
                : 'Your complete training center for staffing and recruitment excellence'
              }
            </p>
          </div>
          {isAdmin && (
            <Button variant="outline" onClick={() => navigate('/rpat/analytics')}>
              <BarChart3 className="h-4 w-4 mr-2" />
              View Analytics
            </Button>
          )}
        </div>

        {/* Stats Overview - Admin sees aggregate, User sees personal */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card 
            className={`bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border-violet-200 dark:border-violet-800 ${isAdmin ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all' : ''}`}
            onClick={() => isAdmin && openDetailModal('topics', 'Topics Completed')}
            data-testid="stat-card-topics"
          >
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-violet-600" />
                <div>
                  <p className="text-xl font-bold">{isAdmin ? adminStats.topics_done : stats.rpat.completed}</p>
                  <p className="text-xs text-muted-foreground">Topics Done</p>
                  {isAdmin && <p className="text-[10px] text-violet-500">Click for details</p>}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 border-blue-200 dark:border-blue-800 ${isAdmin ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all' : ''}`}
            onClick={() => isAdmin && openDetailModal('simulations', 'Recruiter Simulations')}
            data-testid="stat-card-simulations"
          >
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-xl font-bold">{isAdmin ? adminStats.simulations : stats.recruiterSims.completed}</p>
                  <p className="text-xs text-muted-foreground">Simulations</p>
                  {isAdmin && <p className="text-[10px] text-blue-500">Click for details</p>}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-emerald-200 dark:border-emerald-800 ${isAdmin ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all' : ''}`}
            onClick={() => isAdmin && openDetailModal('job_sims', 'Job Simulations')}
            data-testid="stat-card-job-sims"
          >
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-xl font-bold">{isAdmin ? adminStats.job_sims : stats.jobSims.completed}</p>
                  <p className="text-xs text-muted-foreground">Job Sims</p>
                  {isAdmin && <p className="text-[10px] text-emerald-500">Click for details</p>}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-amber-950/30 border-amber-200 dark:border-amber-800 ${isAdmin ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all' : ''}`}
            onClick={() => isAdmin && openDetailModal('labs', 'Sourcing Labs')}
            data-testid="stat-card-labs"
          >
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Search className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="text-xl font-bold">{isAdmin ? adminStats.labs_done : stats.sourcingLabs.completed}</p>
                  <p className="text-xs text-muted-foreground">Labs Done</p>
                  {isAdmin && <p className="text-[10px] text-amber-500">Click for details</p>}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30 border-rose-200 dark:border-rose-800 ${isAdmin ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all' : ''}`}
            onClick={() => isAdmin && openDetailModal('badges', 'Badges Earned')}
            data-testid="stat-card-badges"
          >
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5 text-rose-600" />
                <div>
                  <p className="text-xl font-bold">{isAdmin ? adminStats.badges : stats.globalModules.badges}</p>
                  <p className="text-xs text-muted-foreground">Badges</p>
                  {isAdmin && <p className="text-[10px] text-rose-500">Click for details</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Simulations & Practice Section - 4 cards in a row */}
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-500" />
            Simulations & Practice
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {simulationsModules.map((module) => {
              const Icon = module.icon;
              return (
                <Card 
                  key={module.id}
                  className={`relative overflow-hidden transition-all hover:shadow-lg hover:-translate-y-1 cursor-pointer group ${module.bgColor} ${module.borderColor}`}
                  onClick={() => navigate(module.path)}
                  data-testid={`training-module-${module.id}`}
                >
                  {/* Gradient Banner */}
                  <div className={`h-1.5 bg-gradient-to-r ${module.color}`} />
                  
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${module.color} text-white shadow-lg group-hover:scale-110 transition-transform`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {module.badge && (
                          <Badge className="bg-emerald-500 text-white text-xs">
                            {module.badge}
                          </Badge>
                        )}
                        <Badge variant="secondary" className="text-xs">
                          {module.statsValue} {module.statsLabel}
                        </Badge>
                      </div>
                    </div>
                    <CardTitle className="text-lg mt-3 group-hover:text-primary transition-colors">
                      {module.title}
                    </CardTitle>
                    <CardDescription className="text-sm line-clamp-2">
                      {module.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    {/* Progress Bar */}
                    {module.progress !== null && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Progress</span>
                          <span>{Math.round(module.progress)}%</span>
                        </div>
                        <Progress value={module.progress} className="h-1.5" />
                      </div>
                    )}
                    
                    {/* Features */}
                    <div className="flex flex-wrap gap-1.5">
                      {module.features.slice(0, 3).map((feature, i) => (
                        <Badge key={i} variant="outline" className="text-xs font-normal bg-white/50 dark:bg-black/20">
                          {feature}
                        </Badge>
                      ))}
                      {module.features.length > 3 && (
                        <Badge variant="outline" className="text-xs font-normal bg-white/50 dark:bg-black/20">
                          +{module.features.length - 3}
                        </Badge>
                      )}
                    </div>
                    
                    {/* CTA */}
                    <div className="flex items-center justify-between pt-2 border-t border-border/50">
                      <span className="text-sm font-medium text-primary">
                        {module.progress > 0 ? 'Continue' : 'Start'} Training
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Training & Certifications Section */}
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-violet-500" />
            Training & Certifications
          </h2>
          <div className={`grid grid-cols-1 gap-5 ${trainingModules.length >= 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`}>
            {trainingModules.map((module) => {
              const Icon = module.icon;
              return (
                <Card 
                  key={module.id}
                  className={`relative flex flex-col overflow-hidden transition-all hover:shadow-lg hover:-translate-y-1 cursor-pointer group ${module.bgColor} ${module.borderColor}`}
                  onClick={() => navigate(module.path)}
                  data-testid={`training-module-${module.id}`}
                >
                  {/* Gradient Banner */}
                  <div className={`h-1.5 bg-gradient-to-r ${module.color}`} />
                  
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${module.color} text-white shadow-lg group-hover:scale-110 transition-transform`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {module.badge && (
                          <Badge className={module.isPremium ? "bg-amber-500 text-white text-xs" : "bg-emerald-500 text-white text-xs"}>
                            {module.badge}
                          </Badge>
                        )}
                        <Badge variant="secondary" className="text-xs">
                          {module.statsValue} {module.statsLabel}
                        </Badge>
                      </div>
                    </div>
                    <CardTitle className="text-lg mt-3 group-hover:text-primary transition-colors">
                      {module.title}
                    </CardTitle>
                    <CardDescription className="text-sm line-clamp-2">
                      {module.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-4 mt-auto">
                    {/* Progress Bar */}
                    {module.progress !== null && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Progress</span>
                          <span>{Math.round(module.progress)}%</span>
                        </div>
                        <Progress value={module.progress} className="h-1.5" />
                      </div>
                    )}
                    
                    {/* Features */}
                    <div className="flex flex-wrap gap-1.5">
                      {module.features.slice(0, 3).map((feature, i) => (
                        <Badge key={i} variant="outline" className="text-xs font-normal bg-white/50 dark:bg-black/20">
                          {feature}
                        </Badge>
                      ))}
                      {module.features.length > 3 && (
                        <Badge variant="outline" className="text-xs font-normal bg-white/50 dark:bg-black/20">
                          +{module.features.length - 3}
                        </Badge>
                      )}
                    </div>
                    
                    {/* CTA */}
                    <div className="flex items-center justify-between pt-2 border-t border-border/50">
                      <span className="text-sm font-medium text-primary">
                        {module.progress > 0 ? 'Continue' : 'Start'} Training
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickActions.map((action, i) => {
              const Icon = action.icon;
              return (
                <Card 
                  key={i}
                  className="hover:shadow-md transition-all cursor-pointer group hover:border-primary/50"
                  onClick={() => navigate(action.path)}
                  data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
                >
                  <CardContent className="pt-4 pb-3 flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors">
                      <Icon className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{action.label}</p>
                      <p className="text-xs text-muted-foreground">{action.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Admin Section */}
        {isAdmin && (
          <Card className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-950/50 dark:to-gray-950/50 border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Administration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3"
                  onClick={() => navigate('/rpat/analytics')}
                >
                  <BarChart3 className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Analytics Dashboard</p>
                    <p className="text-xs text-muted-foreground">View learner progress & metrics</p>
                  </div>
                </Button>
                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3"
                  onClick={() => navigate('/curriculum-builder')}
                >
                  <FileText className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Curriculum Builder</p>
                    <p className="text-xs text-muted-foreground">Manage training content</p>
                  </div>
                </Button>
                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3"
                  onClick={() => navigate('/settings')}
                >
                  <Settings className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Module Settings</p>
                    <p className="text-xs text-muted-foreground">Configure training modules</p>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Admin Details Modal */}
      <Dialog open={detailModal.open} onOpenChange={(open) => !open && closeDetailModal()}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="admin-details-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {detailModal.title}
            </DialogTitle>
            <DialogDescription>
              Detailed breakdown by user • {detailModal.pagination.total || 0} total records
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            {detailModal.loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : detailModal.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Users className="h-12 w-12 mb-3 opacity-50" />
                <p>No records found</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px] rounded-md border">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      {getColumnsForCategory(detailModal.category).map((col) => (
                        <TableHead 
                          key={col.key} 
                          className={col.sortable ? 'cursor-pointer hover:bg-muted/50 select-none' : ''}
                          onClick={() => col.sortable && handleSortChange(col.key)}
                          data-testid={`modal-col-${col.key}`}
                        >
                          <div className="flex items-center gap-1">
                            {col.label}
                            {col.sortable && detailModal.sortBy === col.key && (
                              <ArrowUpDown className={`h-3 w-3 ${detailModal.sortOrder === 'asc' ? 'rotate-180' : ''}`} />
                            )}
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailModal.items.map((item, idx) => (
                      <TableRow key={item.id || idx} data-testid={`modal-row-${idx}`}>
                        {getColumnsForCategory(detailModal.category).map((col) => (
                          <TableCell key={col.key}>
                            {col.key === 'user_name' ? (
                              <div>
                                <p className="font-medium">{item.user_name || 'Unknown'}</p>
                                <p className="text-xs text-muted-foreground">{item.user_email || ''}</p>
                              </div>
                            ) : col.key === 'score' ? (
                              <Badge 
                                variant={item.score >= 80 ? 'default' : item.score >= 60 ? 'secondary' : 'destructive'}
                                className={item.score >= 80 ? 'bg-green-500' : item.score >= 60 ? 'bg-amber-500' : ''}
                              >
                                {formatCellValue(item, col.key)}
                              </Badge>
                            ) : (
                              <span className="text-sm">{formatCellValue(item, col.key)}</span>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </div>
          
          {/* Pagination Controls */}
          {!detailModal.loading && detailModal.pagination.total_pages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {detailModal.pagination.page} of {detailModal.pagination.total_pages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(detailModal.pagination.page - 1)}
                  disabled={detailModal.pagination.page <= 1}
                  data-testid="modal-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(detailModal.pagination.page + 1)}
                  disabled={!detailModal.pagination.has_more}
                  data-testid="modal-next-page"
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default StaffingTraining;
